import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const ROLE_META = {
  admin: { label: "Admin", className: "bg-zinc-950 text-white" },
  full: { label: "Full Access", className: "bg-[#002FA7] text-white" },
  limited: { label: "Limited", className: "bg-amber-100 text-amber-800 border border-amber-200" },
};

const emptyUser = { phone: "", name: "", role: "limited", crm_access: false, visible_user_ids: [], pin: "" };

function formatPhone(digits) {
  if (!digits) return "";
  const d = String(digits).replace(/\D/g, "");
  if (d.length === 10) return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
  if (d.length === 7) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return d;
}

export default function Users() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyUser);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/users");
    setUsers(data);
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(emptyUser); setOpen(true); };
  const openEdit = (u) => {
    setEditing(u);
    setForm({
      phone: u.phone || "",
      name: u.name || "",
      role: u.role,
      crm_access: !!u.crm_access,
      visible_user_ids: Array.isArray(u.visible_user_ids) ? u.visible_user_ids : [],
      pin: "",
    });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!editing) {
      if (!/^\d{4}$/.test(form.pin || "")) {
        toast.error("PIN must be exactly 4 digits");
        return;
      }
    } else if (form.pin && !/^\d{4}$/.test(form.pin)) {
      toast.error("PIN must be exactly 4 digits");
      return;
    }
    setSaving(true);
    try {
      // Visibility grants and CRM access only apply to non-admin users.
      const isAdminRole = form.role === "admin";
      const payload = {
        name: form.name,
        role: form.role,
        phone: form.phone,
        crm_access: isAdminRole ? true : !!form.crm_access,
        visible_user_ids: isAdminRole ? [] : form.visible_user_ids,
      };
      if (form.pin) payload.pin = form.pin;
      if (editing) {
        await api.patch(`/users/${editing.id}`, payload);
        toast.success(form.pin ? "User updated · PIN reset" : "User updated");
      } else {
        await api.post("/users", { ...payload, phone: form.phone, pin: form.pin });
        toast.success("User created");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (u) => {
    if (u.id === me.id) return toast.error("You cannot delete yourself");
    if (!window.confirm(`Delete ${u.name || u.phone}?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 lg:py-10 max-w-5xl">
      <PageHeader
        eyebrow="Administration"
        title="Users"
        description="Manage team members and their access. Users log in with their phone number."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={openCreate} data-testid="add-user-btn" className="rounded-sm bg-[#002FA7] hover:bg-[#002075] font-semibold">
                <Plus className="w-4 h-4 mr-2" /> Add User
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-sm">
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl tracking-tight">
                  {editing ? "Edit user" : "New user"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={save} className="space-y-4" data-testid="user-form">
                <div>
                  <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Phone</Label>
                  <Input required type="tel" placeholder="555-000-0004" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="user-phone-input" className="rounded-sm border-zinc-300 font-mono" />
                </div>
                <div>
                  <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Name</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name-input" className="rounded-sm border-zinc-300" />
                </div>
                <div>
                  <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">
                    {editing ? "New PIN (leave blank to keep current)" : "PIN"}
                  </Label>
                  <Input
                    type="text"
                    inputMode="numeric"
                    autoComplete="new-password"
                    maxLength={4}
                    pattern="[0-9]{4}"
                    placeholder="4-digit code"
                    value={form.pin}
                    onChange={(e) => setForm({ ...form, pin: e.target.value.replace(/\D/g, "").slice(0, 4) })}
                    data-testid="user-pin-input"
                    className="rounded-sm border-zinc-300 font-mono tracking-widest"
                    required={!editing}
                  />
                  <div className="text-xs text-zinc-500 mt-1">
                    {editing
                      ? "Only fill this in if you want to change or reset the PIN."
                      : "The user will sign in with this PIN. Must be unique across all users."}
                  </div>
                </div>
                <div>
                  <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Role</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger className="rounded-sm border-zinc-300" data-testid="user-role-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Admin — full access + manage users</SelectItem>
                      <SelectItem value="full">Full Access — everything except users</SelectItem>
                      <SelectItem value="limited">Limited — timecard + supply requests only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {form.role !== "admin" && (
                  <div>
                    <label className="flex items-start gap-2 cursor-pointer" data-testid="user-crm-toggle-wrap">
                      <input
                        type="checkbox"
                        checked={!!form.crm_access}
                        onChange={(e) => setForm({ ...form, crm_access: e.target.checked })}
                        data-testid="user-crm-access-checkbox"
                        className="mt-0.5 h-4 w-4 rounded-sm border-zinc-400 accent-[#002FA7]"
                      />
                      <span className="text-sm text-zinc-800">
                        <span className="font-semibold">CRM access</span>
                        <span className="block text-xs text-zinc-500">Allow this user to view and manage the CRM leads page.</span>
                      </span>
                    </label>
                  </div>
                )}
                {form.role !== "admin" && (
                  <div>
                    <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Can view timecards of</Label>
                    <div className="mt-1 border border-zinc-300 rounded-sm max-h-40 overflow-y-auto divide-y divide-zinc-100" data-testid="user-visible-users-list">
                      {users
                        .filter((u) => u.id !== (editing?.id || "") && u.role !== "admin")
                        .map((u) => {
                          const checked = form.visible_user_ids.includes(u.id);
                          return (
                            <label
                              key={u.id}
                              className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-zinc-50"
                              data-testid={`user-visible-option-${u.phone}`}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={(e) => {
                                  const next = e.target.checked
                                    ? [...form.visible_user_ids, u.id]
                                    : form.visible_user_ids.filter((id) => id !== u.id);
                                  setForm({ ...form, visible_user_ids: next });
                                }}
                                data-testid={`user-visible-checkbox-${u.phone}`}
                                className="h-4 w-4 rounded-sm border-zinc-400 accent-[#002FA7]"
                              />
                              <span className="text-sm text-zinc-800">
                                {u.name || u.phone}
                                <span className="text-xs text-zinc-500 font-mono ml-2">{u.phone}</span>
                              </span>
                            </label>
                          );
                        })}
                      {users.filter((u) => u.id !== (editing?.id || "") && u.role !== "admin").length === 0 && (
                        <div className="px-3 py-2 text-xs text-zinc-500">No other non-admin users available.</div>
                      )}
                    </div>
                    <div className="mt-1 text-xs text-zinc-500">
                      This user will see the selected employees&apos; timecards alongside their own (view only).
                    </div>
                  </div>
                )}
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
                  <Button type="submit" disabled={saving} data-testid="user-save-btn" className="rounded-sm bg-[#002FA7] hover:bg-[#002075] font-semibold">
                    {saving ? "Saving..." : editing ? "Save" : "Create user"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="mt-6 bg-white border border-zinc-200 rounded-sm overflow-x-auto">
        <table className="w-full min-w-[640px]">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr>
              {["Name", "Phone", "Role", "PIN", ""].map((h) => (
                <th key={h} className="text-left px-4 py-3 text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-200">
            {users.map((u) => {
              const meta = ROLE_META[u.role] || { label: u.role, className: "bg-zinc-100 text-zinc-700" };
              return (
                <tr key={u.id} className="hover:bg-zinc-50" data-testid={`user-row-${u.phone}`}>
                  <td className="px-4 py-3 text-sm font-semibold text-zinc-950">{u.name || "—"}</td>
                  <td className="px-4 py-3 text-sm font-mono text-zinc-700">{formatPhone(u.phone)}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-block px-2 py-0.5 text-[10px] tracking-[0.15em] uppercase font-bold rounded-sm ${meta.className}`}>
                      {meta.label}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {u.has_pin ? (
                      <span className="text-[10px] tracking-[0.15em] uppercase font-bold text-emerald-700" data-testid={`user-pin-status-${u.phone}`}>Set</span>
                    ) : (
                      <span className="text-[10px] tracking-[0.15em] uppercase font-bold text-[#FF2400]" data-testid={`user-pin-status-${u.phone}`}>Missing</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => openEdit(u)} data-testid={`edit-user-${u.phone}`} className="text-zinc-500 hover:text-zinc-950 mr-2">
                      <Pencil className="w-4 h-4" />
                    </button>
                    {u.id !== me.id && (
                      <button onClick={() => remove(u)} data-testid={`delete-user-${u.phone}`} className="text-zinc-500 hover:text-[#FF2400]">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
